import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
  getInputLabelProps,
  getInputProps,
  PopperFullWidth,
} from "../../../common/appStyles";
import localize from "../../../common/localize";
import { useEffect, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Combobox from "../../../components/common/Combobox";
import useGlobalContext from "../../../hooks/useGlobalContext";
import {
  getCurrentDays,
  GetDateInDDMMYYYYFormat,
  GetDateInYYYYMMDDWithoutTimeFormat,
  getDaysToError,
  getDaysToWarning,
} from "../../../common/utils";
import { getUserName } from "../../../common/EntitiesManagement";
import { TabContext, TabPanel } from "@mui/lab";
import ScrollablePageTab from "../../dynamicForm/components/ScrollablePageTab";
import RequestNotes from "./RequestNotes";
import RequestStatusChange from "./RequestStatusChange";
import RequestStatus from "../../../components/common/RequestStatus";
import WarningErrorStatus from "../../../components/common/WarningErrorStatus";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";
import { Add } from "@mui/icons-material";
import EventSearch from "../../../components/common/EventSearch";
import ExpandableView from "../../../components/common/ExpandableView";

export default function AddOrEditGeneralRequests({
  model,
  setModel,
  readOnly,
}) {
  const { config, REQUEST_TYPE_ENDPOINT, CLIENTS_API, PERSONAL_API } =
    useGlobalContext();
  const [requestTypes, setRequestTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [personel, setPersonel] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchList, setSearchList] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);

  const getRequestTypes = async () => {
    const configData = {
      headers: {
        ...config.headers,
        pagination: -1,
      },
    };

    const result = await axios.get(`${REQUEST_TYPE_ENDPOINT}`, configData);

    if (result?.data?.Data?.length > 0) {
      setRequestTypes(...[result.data.Data]);
    }
  };

  const getClients = async () => {
    const configData = {
      headers: {
        ...config.headers,
        pagination: -1,
      },
    };

    const result = await axios.get(`${CLIENTS_API}`, configData);

    if (result?.data?.Data?.length > 0) {
      setClients(...[result.data.Data]);
    }
  };

  const getPersonel = async () => {
    const configData = {
      headers: {
        ...config.headers,
        pagination: -1,
      },
    };

    const result = await axios.get(`${PERSONAL_API}`, configData);

    if (result?.data?.Data?.length > 0) {
      setPersonel(...[result.data.Data]);
    }
  };

  const handleRequestTypeChange = (type) => {
    if (!type) {
      setModel({
        ...model,
        RequestType: null,
        RequestTypeId: 0,
      });

      return;
    }

    setModel({
      ...model,
      RequestType: type,
      RequestTypeId: type.Id,
    });
  };

  const handleClientChange = (client) => {
    if (!client) {
      setModel({
        ...model,
        ClientId: 0,
        Client: null,
      });
      return;
    }

    setModel({
      ...model,
      Client: client,
      ClientId: client.Id,
    });
  };

  const handlePersonalAssigned = (personal) => {
    if (!personal) {
      setModel({
        ...model,
        PersonalAssigned: null,
        PersonalId: 0,
      });
      return;
    }

    setModel({
      ...model,
      PersonalAssigned: personal,
      PersonalId: personal.id,
    });
  };

  const handleExpectedSolutionDate = (dateValue) => {
    if (!dateValue) {
      const newDate = new Date();
      setModel({
        ...model,
        ExpectedSolutionDate: GetDateInYYYYMMDDWithoutTimeFormat(
          newDate,
          false,
          false
        ),
        ExpectedSolutionDateValue: newDate,
      });
      return;
    }

    setModel({
      ...model,
      ExpectedSolutionDate: GetDateInYYYYMMDDWithoutTimeFormat(
        dateValue,
        false,
        false
      ),
      ExpectedSolutionDateValue: dateValue,
    });
  };

  const handleChange = (e) => {
    setModel({
      ...model,
      Observation: e.target.value,
    });
  };

  useEffect(() => {
    getRequestTypes();
    getPersonel();
    getClients();
  }, []);

  const getComponents = () => {
    const result = [
      {
        Name: localize.general_information,
        Component: readOnly ? (
          getReadOnlyGeneralInformationUI()
        ) : (
          <Box sx={{ width: "100%" }} className="flex flex-col gap-3 mt-2">
            <FormControl required fullWidth>
              <Combobox
                disabled={
                  readOnly ||
                  !requestTypes ||
                  requestTypes?.length === 0 ||
                  (model.Id > 0 && model.CreatedBy !== getUserName())
                }
                isRequired
                id="Type"
                fieldLabel={localize.request_type}
                fieldOptions={requestTypes ?? []}
                fieldValue={model.Type}
                handleValueChange={handleRequestTypeChange}
              />
            </FormControl>

            <FormControl required fullWidth>
              <Combobox
                disabled={
                  readOnly ||
                  !personel ||
                  personel?.length === 0 ||
                  (model.Id > 0 && model.CreatedBy !== getUserName())
                }
                isRequired
                id="PersonalAssigned"
                fieldLabel={localize.assigned_to}
                fieldOptions={personel ?? []}
                fieldValue={model.PersonalAssigned}
                handleValueChange={handlePersonalAssigned}
                customOptionLabel={(option) =>
                  `${option.Identification} - ${option.SurName} ${option.LastName}`
                }
              />
            </FormControl>

            <Box className="flex flex-col gap-2 my-2">
              <Divider />
              <Box className="flex gap-2 justify-between items-center">
                <PageTitleSubtitle
                  title={localize.service_order}
                  subtitle={
                    <Box>
                      {model.Event ? (
                        <Link
                          color="primary"
                          underline="always"
                          href="#"
                          onClick={() => {
                            window.open(
                              `/events/detail/${model.Event.Id}`,
                              "_blank"
                            );
                          }}
                        >{`${model.Event.Code}`}</Link>
                      ) : (
                        <label className="text-xs">
                          {localize.not_assigned}
                        </label>
                      )}
                    </Box>
                  }
                  useEventTitleStyle
                  useFieldTitleStyle
                />

                {!readOnly && (
                  <Box className="flex gap-2">
                    <Button
                      size="medium"
                      variant="contained"
                      color="success"
                      onClick={() => {
                        setShowSearch(true);
                      }}
                    >
                      <span className="mr-2">{`${localize.assign}`}</span>
                      <Add style={{ width: "16px", height: "16px" }} />
                    </Button>

                    {model?.Event && (
                      <Button
                        size="medium"
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setSearchList([]);
                        }}
                      >
                        <span className="mr-2">{`${localize.unassign}`}</span>
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
              <Divider />
            </Box>

            <FormControl required fullWidth>
              <Combobox
                disabled={
                  readOnly ||
                  !clients ||
                  clients?.length === 0 ||
                  (model.Id > 0 && model.CreatedBy !== getUserName())
                }
                id="Client"
                fieldLabel={localize.client}
                fieldOptions={clients ?? []}
                fieldValue={model.Client}
                handleValueChange={handleClientChange}
                customOptionLabel={(option) =>
                  `${option.Identification ?? ""}${
                    option.Name ? " - " + option.Name : ""
                  }`
                }
              />
            </FormControl>

            <FormControl required fullWidth>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disabled={
                    readOnly ||
                    (model.Id > 0 && model.CreatedBy !== getUserName())
                  }
                  label={localize.expected_attention_date}
                  className="w-full"
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  PopperComponent={PopperFullWidth}
                  value={model?.ExpectedSolutionDateValue}
                  onChange={handleExpectedSolutionDate}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "",
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        style: { fontSize: "1em", paddingBottom: "10px" },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>

            <FormControl fullWidth>
              <TextField
                disabled={
                  readOnly ||
                  (model.Id > 0 && model.CreatedBy !== getUserName())
                }
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={model.Observation}
                onChange={handleChange}
                size="small"
                fullWidth
                multiline
                rows={3}
                id="Observation"
                label={localize.observations}
                name="Observation"
                variant="outlined"
              />
            </FormControl>

            {model?.Id > 0 && (
              <Alert severity="info" sx={{ width: "100%" }}>
                <Box className="flex gap-2">
                  <label className="text-xs">
                    <b>{localize.created_by}:</b>
                  </label>
                  <label className="text-xs">{model?.CreatedBy}</label>
                </Box>
              </Alert>
            )}
          </Box>
        ),
      },
    ];

    if (model?.Id > 0) {
      result.push({
        Name: localize.notes,
        Component: <RequestNotes requestModel={model} />,
      });

      result.push({
        Name: localize.status_changes,
        Component: (
          <RequestStatusChange
            requestModel={model}
            setRequestModel={setModel}
          />
        ),
      });
    }

    return result;
  };

  const getReadOnlyGeneralInformationUI = () => {
    return (
      <Box className="flex flex-col gap-4">
        <Box
          className="flex w-full text-xs gap-2 justify-between items-center"
          sx={{ flexDirection: "row-reverse" }}
        >
          <RequestStatus status={model?.Status} />
          <WarningErrorStatus
            currentValue={getCurrentDays(model)}
            limitForWarning={getDaysToWarning(model)}
            limitForError={getDaysToError(model)}
            isSucceeded={model?.Status === 2}
            isFailed={model?.Status === 3}
            showStatusName
          />
        </Box>
        <Divider />
        <Box className="flex justify-between">
          <Box className="two_columns_grid">
            <label className="text-xs">
              <b>{localize.id}:</b>
            </label>
            <label className="text-xs">{model.Id}</label>
            <label className="text-xs">
              <b>{localize.event}:</b>
            </label>
            <label className="text-xs">
              {model.Event ? (
                <Link
                  color="primary"
                  underline="always"
                  href="#"
                  onClick={() => {
                    window.open(`/events/detail/${model.Event.Id}`, "_blank");
                  }}
                >{`${model.Event.Code}`}</Link>
              ) : (
                localize.not_assigned
              )}
            </label>
            <label className="text-xs">
              <b>{localize.request_type}:</b>
            </label>
            <label className="text-xs">{model.Type?.Name}</label>
            <label className="text-xs">
              <b>{localize.assigned_to}:</b>
            </label>
            <label className="text-xs">{`${model?.PersonalAssigned?.Identification} - ${model.PersonalAssigned?.SurName} ${model.PersonalAssigned?.LastName}`}</label>

            {model.Client && (
              <label className="text-xs">
                <b>{localize.client}:</b>
              </label>
            )}
            {model.Client && (
              <label className="text-xs">{`${model.Client?.Identification} - ${model.Client?.Name}`}</label>
            )}

            <label className="text-xs">
              <b>{localize.expected_attention_date}:</b>
            </label>
            <label className="text-xs">
              {GetDateInDDMMYYYYFormat(model.ExpectedSolutionDateValue)}
            </label>

            <label className="text-xs">
              <b>{localize.created_by}:</b>
            </label>
            <label className="text-xs">{model.CreatedBy}</label>
          </Box>
        </Box>

        {model.Observation && (
          <Alert severity="info" className="w-full">
            <label className="text-xs">
              <b>{localize.observations}: </b>
              {model.Observation}
            </label>
          </Alert>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (searchList && searchList.length > 0) {
      console.log(searchList[0]);
      setModel({
        ...model,
        Event: searchList[0],
        EventId: searchList[0].Id,
        Client: searchList[0]?.Client
          ? { ...searchList[0]?.Client }
          : { ...model.Client },
        ClientId: searchList[0]?.Client?.Id ?? model.Client?.Id,
      });

      return;
    }

    if (firstLoad) {
      setFirstLoad(false);
      return;
    }

    setModel({
      ...model,
      Event: undefined,
      EventId: 0,
    });
  }, [searchList]);

  return (
    <Box sx={{ width: "100%" }}>
      {showSearch && (
        <EventSearch
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          setResultList={setSearchList}
          singleSearch
        />
      )}

      <ExpandableView components={getComponents()} />
    </Box>
  );
}
